import React from "react";

import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    FormControl,
    MenuItem,
    Paper,
    Select,
    Stack,
    Typography,
} from "@mui/material";

import {
    Groups as GroupsIcon,
} from "@mui/icons-material";

import {
    BRAND_CHECKBOX_SX,
    getProficiencySelectSx,
} from "./batchAssessmentColors";

import AssessmentTabEmptyState from "./AssessmentTabEmptyState";


const PROFICIENCY_OPTIONS = [
    "Emerging",
    "Developing",
    "Proficient",
    "Extending",
];

const OVERALL_ASSESSMENT_OPTIONS = [
    "Emerging",
    "Developing",
    "Proficient",
    "Extending",
];


const BatchEntryPanel = ({
    selectedStudentCount = 0,

    skills = [],
    dokLevels = [],

    selectedSkillIds = [],
    dokProficiency = {},
    overallAssessment = "",
    hasBatchChanges = false,

    onToggleSkill,
    onDOKProficiencyChange,
    onOverallAssessmentChange,

    onApply,
    onClear,
}) => {

    const canApply =
        selectedStudentCount > 0;

    if (!canApply) {
        return (
            <AssessmentTabEmptyState
                icon={GroupsIcon}
                title="Select students on the left to apply skills"
                description="Check the boxes next to student names in the list to choose who this batch update applies to."
                tip="Once skills are applied, you can remove them for an individual student anytime from the Create Student Assessment tab."
            />
        );
    }


    return (
        <Paper
            elevation={0}
            sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 3,
                overflow: "hidden",
            }}
        >
            {/* =================================================
                Header
            ================================================= */}

{/* =================================================
    Header + Actions
================================================= */}

<Box
    sx={{
        px: {
            xs: 2,
            sm: 2.5,
        },
        py: 1.5,
        backgroundColor: "#F7F9F2",
        borderBottom: "1px solid",
        borderColor: "divider",
    }}
>
    <Stack
        direction={{
            xs: "column",
            md: "row",
        }}
        justifyContent="space-between"
        alignItems={{
            xs: "stretch",
            md: "center",
        }}
        spacing={1.5}
    >
        <Box>
            <Stack
                direction="row"
                spacing={1}
                alignItems="center"
            >
                <GroupsIcon
                    sx={{
                        color: "#738B3B",
                        fontSize: 21,
                    }}
                />

                <Typography
                    fontWeight={700}
                    fontSize="1rem"
                >
                    Batch Entry
                </Typography>

                <Chip
                    size="small"
                    label={`${selectedStudentCount} selected`}
                    sx={{
                        height: 23,
                        backgroundColor: "#DCE6C5",
                        color: "#425126",
                        fontWeight: 700,
                        fontSize: "0.7rem",
                    }}
                />
            </Stack>

            <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                    display: "block",
                    mt: 0.35,
                }}
            >
                Apply common Skill achievement, DOK
                proficiency, or overall assessment to
                selected students.
            </Typography>
        </Box>


        {/* Batch Actions */}

        <Stack
            direction="row"
            spacing={1}
            justifyContent={{
                xs: "flex-start",
                md: "flex-end",
            }}
        >
            <Button
                variant="text"
                disabled={
                    !canApply ||
                    !hasBatchChanges
                }
                onClick={onClear}
                sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    color: "#647044",
                    px: 1.75,
                    borderRadius: 2,

                    "&:hover": {
                        backgroundColor: "#EEF3E4",
                    },
                }}
            >
                Clear
            </Button>

            <Button
                variant="contained"
                disabled={
                    !canApply ||
                    !hasBatchChanges
                }
                onClick={onApply}
                sx={{
                    textTransform: "none",
                    fontWeight: 700,

                    px: 2.25,
                    py: 0.9,

                    borderRadius: 2,

                    backgroundColor: "#738B3B",

                    boxShadow:
                        "0 2px 6px rgba(70, 85, 35, 0.18)",

                    "&:hover": {
                        backgroundColor: "#647A33",
                        boxShadow:
                            "0 3px 8px rgba(70, 85, 35, 0.24)",
                    },
                }}
            >
                Apply to{" "}
                {selectedStudentCount} Student
                {selectedStudentCount === 1
                    ? ""
                    : "s"}
            </Button>
        </Stack>
    </Stack>
</Box>
            {/* =================================================
                Batch Entry Content
            ================================================= */}

            <Stack
                spacing={2.5}
                sx={{
                    p: {
                        xs: 2,
                        sm: 2.5,
                    },
                }}
            >

                {/* =============================================
                    Skills grouped by DOK
                ============================================= */}

                {dokLevels.map((dokLevel) => {

                    const dokSkills =
                        skills.filter(
                            (skill) =>
                                skill.dok_level?.level ===
                                dokLevel.level
                        );

                    const selectedCount =
                        dokSkills.filter(
                            (skill) =>
                                selectedSkillIds.includes(
                                    skill.id
                                )
                        ).length;

                    return (
                        <Box
                            key={dokLevel.id}
                            sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 2.5,
                                overflow: "hidden",
                            }}
                        >

                            {/* DOK Header */}

                            <Stack
                                direction={{
                                    xs: "column",
                                    sm: "row",
                                }}
                                justifyContent="space-between"
                                alignItems={{
                                    xs: "stretch",
                                    sm: "center",
                                }}
                                spacing={1.5}
                                sx={{
                                    px: 2,
                                    py: 1.5,
                                    backgroundColor:
                                        "#F7F7F2",
                                }}
                            >
                                <Box>
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                    >
                                        <Chip
                                            size="small"
                                            label={
                                                `DOK ${dokLevel.level}`
                                            }
                                            sx={{
                                                fontWeight: 700,
                                            }}
                                        />

                                        <Typography
                                            fontWeight={700}
                                            fontSize="0.95rem"
                                        >
                                            {
                                                dokLevel.description
                                            }
                                        </Typography>
                                    </Stack>

                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        {selectedCount} Skill
                                        {selectedCount === 1
                                            ? ""
                                            : "s"}{" "}
                                        selected for batch
                                        application
                                    </Typography>
                                </Box>


                                {/* DOK Proficiency */}

                                <FormControl
                                    size="small"
                                    sx={{
                                        minWidth: {
                                            xs: "100%",
                                            sm: 180,
                                        },
                                    }}
                                >
                                    <Select
                                        displayEmpty
                                        value={
                                            dokProficiency[
                                                dokLevel.level
                                            ] || ""
                                        }
                                        disabled={
                                            !canApply
                                        }
                                        onChange={(event) =>
                                            onDOKProficiencyChange(
                                                dokLevel.level,
                                                event.target.value
                                            )
                                        }
                                        sx={getProficiencySelectSx(
                                            dokProficiency[
                                                dokLevel.level
                                            ] || ""
                                        )}
                                    >
                                        <MenuItem value="">
                                            <em>
                                                No batch change
                                            </em>
                                        </MenuItem>

                                        {PROFICIENCY_OPTIONS.map(
                                            (option) => (
                                                <MenuItem
                                                    key={option}
                                                    value={option}
                                                >
                                                    {option}
                                                </MenuItem>
                                            )
                                        )}
                                    </Select>
                                </FormControl>
                            </Stack>


                            {/* Skills */}

                            <Stack>
                                {dokSkills.map(
                                    (skill) => {

                                        const checked =
                                            selectedSkillIds.includes(
                                                skill.id
                                            );

                                        return (
                                            <Box
                                                key={
                                                    skill.id
                                                }
                                                sx={{
                                                    px: 2,
                                                    py: 1,
                                                    borderTop:
                                                        "1px solid",
                                                    borderColor:
                                                        "divider",
                                                }}
                                            >
                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    alignItems="flex-start"
                                                >
                                                    <Checkbox
                                                        checked={
                                                            checked
                                                        }
                                                        disabled={
                                                            !canApply
                                                        }
                                                        onChange={() =>
                                                            onToggleSkill(
                                                                skill.id
                                                            )
                                                        }
                                                        sx={{
                                                            mt: -0.5,
                                                            ...BRAND_CHECKBOX_SX,
                                                        }}
                                                    />

                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            pt: 0.45,
                                                            lineHeight:
                                                                1.5,
                                                        }}
                                                    >
                                                        {
                                                            skill.skill_description
                                                        }
                                                    </Typography>
                                                </Stack>
                                            </Box>
                                        );
                                    }
                                )}
                            </Stack>
                        </Box>
                    );
                })}


                {/* =============================================
                    Overall Assessment
                ============================================= */}

                <Box>
                    <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        sx={{
                            mb: 0.75,
                        }}
                    >
                        Overall Proficiency
                    </Typography>

                    <FormControl
                        fullWidth
                        size="small"
                    >
                        <Select
                            displayEmpty
                            value={
                                overallAssessment
                            }
                            disabled={
                                !canApply
                            }
                            onChange={(event) =>
                                onOverallAssessmentChange(
                                    event.target.value
                                )
                            }
                            sx={getProficiencySelectSx(
                                overallAssessment
                            )}
                        >
                            <MenuItem value="">
                                <em>
                                    No batch change
                                </em>
                            </MenuItem>

                            {OVERALL_ASSESSMENT_OPTIONS.map(
                                (option) => (
                                    <MenuItem
                                        key={option}
                                        value={option}
                                    >
                                        {option}
                                    </MenuItem>
                                )
                            )}
                        </Select>
                    </FormControl>
                </Box>


                {/* =============================================
                    Important Safeguard
                ============================================= */}

                {selectedStudentCount > 0 && (
                    <Alert severity="info">
                        These selections will update the
                        editable assessment state for{" "}
                        <strong>
                            {selectedStudentCount}
                        </strong>{" "}
                        selected student
                        {selectedStudentCount === 1
                            ? ""
                            : "s"}.
                        You can review and adjust each student
                        individually before saving.
                    </Alert>
                )}


                {/* =============================================
                    Actions
                ============================================= */}

            

            </Stack>
        </Paper>
    );
};

export default BatchEntryPanel;